// Типы для гибкой настройки
export type DataRow = Record<string, any>;

export interface PivotConfig {
  xFields: string[]; // Поля, которые уйдут в заголовки колонок (X)
  yFields: string[]; // Поля, которые уйдут в заголовки строк (Y)
  // Функция агрегации — принимает массив строк, попавших в ячейку
  bodyExpr: (rows: DataRow[]) => string | number; 
}

// Структура дерева для Y-оси (чтобы поддержать вложенность строк)
export interface PivotNode {
  value: any;
  subNodes: Map<any, PivotNode>;
  rows: DataRow[]; // Оригинальные данные, дошедшие до этого узла
}

/**
 * Находит уникальные комбинации X-полей с дедупликацией и сортировкой.
 */
export function getUniqueCombinations(data: DataRow[], fields: string[]): any[][] {
  if (!fields || fields.length === 0) return [];

  // Быстрая ветка для одной колонки на X-оси (например, только 'date')
  if (fields.length === 1) {
    const field = fields[0];
    const uniqueValues = Array.from(new Set(data.map(row => row[field])));
    return uniqueValues.sort().map(val => [val]);
  }

  // Сложная ветка для составной X-оси (например, ['shop', 'date'])
  const combinationMap = new Map<string, any[]>();

  for (const row of data) {
    const combination = fields.map(field => row[field]);
    const key = JSON.stringify(combination);

    if (!combinationMap.has(key)) {
      combinationMap.set(key, combination);
    }
  }

  return Array.from(combinationMap.values()).sort((a, b) => {
    for (let i = 0; i < a.length; i++) {
      if (a[i] < b[i]) return -1;
      if (a[i] > b[i]) return 1;
    }
    return 0;
  });
}

/**
 * Функция группировки данных. 
 * Возвращает уникальные комбинации X-оси и древовидную структуру Y-оси.
 */
export function buildPivotData(data: DataRow[], config: PivotConfig) {
  const { xFields, yFields } = config;

  // 1. Собираем уникальные комбинации для X-оси (Колонки) с дедупликацией
  const uniqueXCombinations = getUniqueCombinations(data, xFields);

  // 2. Строим дерево для Y-оси (Строки)
  const rootNode: PivotNode = { value: 'root', subNodes: new Map(), rows: data };

  for (const row of data) {
    let currentNode = rootNode;
    for (const yField of yFields) {
      const val = row[yField];
      if (!currentNode.subNodes.has(val)) {
        currentNode.subNodes.set(val, { value: val, subNodes: new Map(), rows: [] });
      }
      currentNode = currentNode.subNodes.get(val)!;
      currentNode.rows.push(row);
    }
  }

  return { uniqueXCombinations, rootNode };
}